const APPLE_SCRIPT = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const OAUTH_RESULT_KEY = 'hc_google_oauth_result';
const OAUTH_RETURN_KEY = 'hc_oauth_return';
const OAUTH_STATE_KEY = 'hc_oauth_state';
const OAUTH_NONCE_KEY = 'hc_oauth_nonce';

function googleClientId() {
  return (
    String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim() ||
    '704171280414-j05liginmmtv42rgr6gslspflad22f2m.apps.googleusercontent.com'
  );
}

function appleClientId() {
  return String(process.env.REACT_APP_APPLE_CLIENT_ID || '').trim() || 'com.tanlenon.homecrowd.web';
}

function oauthCallbackUrl() {
  return `${window.location.origin}/oauth-callback.html`;
}

function appleRedirectUrl() {
  return `${window.location.origin}/oauth-callback`;
}

function isIOS() {
  const ua = String(navigator.userAgent || '');
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && Number(navigator.maxTouchPoints || 0) > 1;
}

function isAndroid() {
  return /Android/i.test(String(navigator.userAgent || ''));
}

export function shouldShowAppleSignIn() {
  if (typeof navigator === 'undefined') return false;
  if (isAndroid()) return false;
  if (isIOS()) return true;
  return /Macintosh/i.test(String(navigator.userAgent || ''));
}

function cancelledError() {
  const error = new Error('cancelled');
  error.cancelled = true;
  return error;
}

function randomString() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => `0${byte.toString(16)}`.slice(-2)).join('');
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load sign-in.')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = () => reject(new Error('Could not load sign-in.'));
    document.head.appendChild(script);
  });
}

function takeSessionJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
}

export function hasPendingGoogleOAuth() {
  try {
    return !!sessionStorage.getItem(OAUTH_RESULT_KEY);
  } catch (_error) {
    return false;
  }
}

function googleAuthUrl(state, nonce) {
  const params = new URLSearchParams({
    client_id: googleClientId(),
    redirect_uri: oauthCallbackUrl(),
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce,
    state,
    prompt: 'select_account',
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

function redirectForOAuth(url) {
  window.location.assign(url);
  return new Promise(() => {});
}

function waitForGooglePopup(popup, expectedState) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer = 0;

    function finish(error, token) {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      try {
        popup.close();
      } catch (_error) {
        void _error;
      }
      if (error) reject(error);
      else resolve(token);
    }

    function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== 'hc-google-oauth') return;
      if (data.state && data.state !== expectedState) {
        finish(new Error('Google sign-in failed.'));
        return;
      }
      if (data.error) {
        finish(data.error === 'access_denied' ? cancelledError() : new Error(data.error_description || 'Google sign-in failed.'));
        return;
      }
      if (!data.id_token) {
        finish(new Error('Google sign-in failed.'));
        return;
      }
      finish(null, data.id_token);
    }

    window.addEventListener('message', onMessage);
    timer = window.setTimeout(() => finish(cancelledError()), 5 * 60 * 1000);
  });
}

export function signInWithGoogle() {
  const pending = takeSessionJson(OAUTH_RESULT_KEY);
  if (pending) {
    if (pending.id_token) return Promise.resolve(pending.id_token);
    if (pending.error === 'access_denied') return Promise.reject(cancelledError());
    return Promise.reject(new Error(pending.error_description || pending.error || 'Google sign-in failed.'));
  }

  const state = randomString();
  const nonce = randomString();
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
    sessionStorage.setItem(OAUTH_RETURN_KEY, window.location.href);
  } catch (_error) {
    void _error;
  }

  const url = googleAuthUrl(state, nonce);
  if (isIOS() || isAndroid()) return redirectForOAuth(url);

  const width = 500;
  const height = 640;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  const popup = window.open(
    url,
    'hc-google-oauth',
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`
  );
  if (!popup || popup.closed) return redirectForOAuth(url);
  return waitForGooglePopup(popup, state);
}

function parseJsonIfNeeded(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch (_error) {
    return value;
  }
}

function pickNamePart(source, keys) {
  if (!source || typeof source !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]] == null ? '' : String(source[keys[i]]).trim();
    if (value) return value;
  }
  return '';
}

function nameFromAppleResponse(response) {
  if (!response || typeof response !== 'object') return { firstName: '', lastName: '' };
  const authorization = response.authorization && typeof response.authorization === 'object' ? response.authorization : {};
  const user = parseJsonIfNeeded(response.user || response.userinfo || authorization.user || null);
  if (!user || typeof user !== 'object') return { firstName: '', lastName: '' };

  const name = parseJsonIfNeeded(user.name || user.fullName || user.full_name || null);
  let firstName = '';
  let lastName = '';
  if (name && typeof name === 'object') {
    firstName = pickNamePart(name, ['firstName', 'first_name', 'givenName', 'given_name', 'nickname']);
    lastName = pickNamePart(name, ['lastName', 'last_name', 'familyName', 'family_name']);
  } else if (typeof name === 'string' && name.trim()) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ');
  }
  if (!firstName) firstName = pickNamePart(user, ['firstName', 'first_name', 'givenName', 'given_name']);
  if (!lastName) lastName = pickNamePart(user, ['lastName', 'last_name', 'familyName', 'family_name']);
  return { firstName, lastName };
}

export async function signInWithApple() {
  if (!shouldShowAppleSignIn()) {
    throw new Error('Sign in with Apple is not available on this device.');
  }
  await loadScript(APPLE_SCRIPT);
  if (!window.AppleID || !window.AppleID.auth) {
    throw new Error('Sign in with Apple is not available.');
  }

  let eventUser = null;
  const onAppleSuccess = (event) => {
    const detail = event && event.detail;
    if (detail && (detail.user || detail.userinfo)) eventUser = detail.user || detail.userinfo;
  };
  document.addEventListener('AppleIDSignInOnSuccess', onAppleSuccess);
  window.AppleID.auth.init({
    clientId: appleClientId(),
    scope: 'name email',
    redirectURI: appleRedirectUrl(),
    usePopup: true,
    state: randomString(),
    nonce: randomString(),
  });

  try {
    let response = await window.AppleID.auth.signIn();
    if (response && !response.user && eventUser) response = { ...response, user: eventUser };
    const identityToken = response && response.authorization && response.authorization.id_token;
    if (!identityToken) throw new Error('Apple sign-in failed.');
    return { identityToken, ...nameFromAppleResponse(response) };
  } catch (error) {
    const code = error && (error.error || error.code || error.message);
    if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize' || code === 'cancelled') {
      throw cancelledError();
    }
    if (error instanceof Error) throw error;
    throw new Error(code || 'Apple sign-in failed.');
  } finally {
    document.removeEventListener('AppleIDSignInOnSuccess', onAppleSuccess);
  }
}
